import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { initials, timeAgo } from "@/lib/format";
import { AuthedImage } from "@/components/AuthedImage";
import { PageLoader, EmptyState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BadgeCheck, Check, X } from "lucide-react";

const TABS = [["pending", "Pending"], ["verified", "Verified"], ["rejected", "Rejected"]];

export function AdminVerify() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [open, setOpen] = useState(null);
  const [reason, setReason] = useState("");

  const { data: rows, isLoading } = useQuery({ queryKey: ["admin-verifications", status], queryFn: () => apiFetch(`/admin/verifications?status=${status}`) });

  const decide = async (approve) => {
    try {
      await apiFetch(`/admin/verifications/${open.id}/decision`, { method: "POST", body: JSON.stringify({ approve, reason: reason.trim() }) });
      toast.success(approve ? "Verified" : "Rejected");
      setOpen(null); setReason("");
      qc.invalidateQueries({ queryKey: ["admin-verifications"] });
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <div className="flex gap-2">{TABS.map(([k, l]) => <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === k ? "bg-brand text-white" : "bg-secondary"}`}>{l}</button>)}</div>
      <div className="mt-4 space-y-2">
        {rows.length === 0 ? <EmptyState icon={BadgeCheck} title="Nothing here" /> : rows.map((r) => (
          <button key={r.id} onClick={() => setOpen(r)} className="flex w-full items-center gap-3 rounded-2xl border border-border p-3 text-left hover:bg-secondary" data-testid={`verify-${r.id}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">{initials(r.user?.name)}</div>
            <div className="min-w-0 flex-1"><p className="truncate font-semibold">{r.user?.name}</p><p className="truncate text-xs text-muted-foreground">{r.id_type} · {r.attempts} attempt(s) · {timeAgo(r.created_at)}</p></div>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {open && (
            <>
              <DialogHeader><DialogTitle>{open.user?.name} · {open.id_type}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-2">
                <div><p className="mb-1 text-xs font-medium text-muted-foreground">Document</p><AuthedImage path={`/verification-doc/${open.id}`} className="aspect-[3/4] w-full rounded-xl object-cover" /></div>
                {open.document_back_url && <div><p className="mb-1 text-xs font-medium text-muted-foreground">Back</p><AuthedImage path={`/verification-doc-back/${open.id}`} className="aspect-[3/4] w-full rounded-xl object-cover" /></div>}
                {open.selfie_url && <div><p className="mb-1 text-xs font-medium text-muted-foreground">Selfie</p><AuthedImage path={`/verification-selfie/${open.id}`} className="aspect-[3/4] w-full rounded-xl object-cover" /></div>}
              </div>
              {status === "pending" && (
                <>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required if rejecting)" className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none" data-testid="verify-reason" />
                  <div className="flex gap-2">
                    <button onClick={() => decide(true)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-price py-2.5 text-sm font-semibold text-white" data-testid="approve-verify"><Check className="h-4 w-4" />Approve</button>
                    <button onClick={() => decide(false)} disabled={!reason.trim()} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-destructive py-2.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="reject-verify"><X className="h-4 w-4" />Reject</button>
                  </div>
                </>
              )}
              {open.decision_reason && <p className="text-sm text-muted-foreground">Reason: {open.decision_reason}</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
